import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { getBuildingById, isEmptyBuildingGeometry } from './storey.helpers';

export {
  getBuildingDisplayName,
  getBuildingLetter,
  getDefaultBuildingName,
  getNextBuildingName,
  withPersistentBuildingNames,
} from './building-name.helpers';

/**
 * A new building may only be started once every existing building has at
 * least one completed footprint. This prevents accumulating empty buildings
 * that the user has not finished drawing.
 */
export const canAddBuilding = (buildings: Building[]): boolean =>
  buildings.every((building) => !isEmptyBuildingGeometry(building));

/** True when at least one building has a completed storey footprint. */
export const hasAnyCompletedStorey = (buildings: Building[]): boolean =>
  buildings.some((building) => !isEmptyBuildingGeometry(building));

/**
 * Remove the building being left when it still has no completed footprint.
 * The destination must exist and the design always keeps at least one building.
 */
export const removeEmptyBuildingOnNavigation = (
  design: BuildingDesign,
  currentBuildingId: string | undefined,
  nextBuildingId: string,
): BuildingDesign => {
  const currentBuilding = getBuildingById(design, currentBuildingId);
  const nextBuilding = getBuildingById(design, nextBuildingId);
  if (
    !currentBuilding ||
    !nextBuilding ||
    currentBuilding === nextBuilding ||
    design.buildings.length <= 1 ||
    !isEmptyBuildingGeometry(currentBuilding)
  ) {
    return design;
  }

  return {
    ...design,
    buildings: design.buildings.filter(
      (building) => building !== currentBuilding,
    ),
  };
};

/**
 * True when selecting {@link nextBuildingId} should move the storey list to
 * that building's first storey. The same building — including an unset
 * current id that already means the first building — keeps the current storey.
 */
export const shouldSwitchStoreyOnBuildingSelect = (
  nextBuildingId: string,
  currentBuildingId: string | undefined,
  firstBuildingId: string | undefined,
): boolean => nextBuildingId !== (currentBuildingId ?? firstBuildingId);
